import type { ZudokuPlugin } from "zudoku";

const STORAGE_KEY = "zudoku-selected-server";

/**
 * Zudoku plugin that reads an `endpoint` query parameter from the URL
 * and pre-selects the corresponding server in the API reference.
 *
 * Example URL:
 *   /products/tax-collection-gateway/api-reference/assessments?endpoint=https://api.pilot.tcg.emtech.com#create-tax-assessment
 *
 * The plugin extracts the origin (e.g. "https://api.pilot.tcg.emtech.com")
 * from the `endpoint` value, persists it to localStorage for Zudoku's
 * Zustand store, then redirects to the clean URL (without the query param)
 * so the store hydrates with the correct server on load.
 */
export const endpointSelectorPlugin: ZudokuPlugin = {
  events: {
    location: ({ to }) => {
      const params = new URLSearchParams(to.search);
      const endpoint = params.get("endpoint");

      if (!endpoint) return;

      try {
        const url = new URL(endpoint);
        const serverUrl =
          url.origin + (url.pathname !== "/" ? url.pathname : "");

        // Persist in the format Zustand's persist middleware expects
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ state: { selectedServer: serverUrl }, version: 0 }),
        );

        // Build clean URL: strip the endpoint param, keep hash from the
        // endpoint value (e.g. #create-tax-assessment) for anchor nav
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete("endpoint");
        if (url.hash) {
          cleanUrl.hash = url.hash;
        }

        // Redirect so Zustand hydrates from localStorage with the
        // correct server already set. replace() avoids a back-button entry.
        window.location.replace(cleanUrl.toString());
      } catch {
        // Invalid URL in the query param — ignore silently
      }
    },
  },
};
